import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ArrowLeft, Users, UserPlus, Mail, Loader2, Check, Clock, 
  RefreshCw, User, Film, Trash2, Link2
} from "lucide-react";
import { getApiUrl } from '@/config/api';
import { getAssetUrl } from '@/utils/studioUtils';

const API = getApiUrl();

export default function TeamManagement() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { getAuthHeaders } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [actors, setActors] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [project, setProject] = useState(null);
  
  // Invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: "",
    character_ids: [],  // Changed to array for multiple characters
    can_see_full_script: false
  });
  
  // Assign dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedActor, setSelectedActor] = useState(null);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState([]);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [actorsRes, charsRes, projectRes] = await Promise.all([
        axios.get(`${API}/director/my-actors`, { headers: getAuthHeaders() }),
        axios.get(`${API}/projects/${projectId}/characters`, { headers: getAuthHeaders() }),
        axios.get(`${API}/projects/${projectId}`, { headers: getAuthHeaders() })
      ]);
      
      setActors(actorsRes.data.actors || []);
      setCharacters(charsRes.data || []);
      setProject(projectRes.data);
    } catch (error) {
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteData.email) {
      toast.error("Please enter an email address");
      return;
    }
    
    setInviting(true);
    try {
      const response = await axios.post(`${API}/director/invite-actor`, {
        email: inviteData.email,
        character_ids: inviteData.character_ids.length > 0 ? inviteData.character_ids : undefined,
        can_see_full_script: inviteData.can_see_full_script
      }, { headers: getAuthHeaders() });
      
      if (response.data.success) {
        toast.success(response.data.is_new 
          ? "Actor invited! They'll receive a setup email." 
          : "Actor added to your team!"
        );
        setInviteOpen(false);
        setInviteData({ email: "", character_ids: [], can_see_full_script: false });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to invite actor");
    } finally {
      setInviting(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedActor || selectedCharacterIds.length === 0) {
      toast.error("Please select at least one character");
      return;
    }
    
    setAssigning(true);
    try {
      // Assign to all selected characters
      for (const charId of selectedCharacterIds) {
        await axios.post(`${API}/director/assign-actor`, {
          actor_id: selectedActor.id,
          character_id: charId
        }, { headers: getAuthHeaders() });
      }
      
      toast.success(`Actor assigned to ${selectedCharacterIds.length} character${selectedCharacterIds.length > 1 ? 's' : ''}!`);
      setAssignOpen(false);
      setSelectedActor(null);
      setSelectedCharacterIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign actor");
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassign = async (actorId, characterId) => {
    try {
      await axios.post(`${API}/director/unassign-actor`, {
        actor_id: actorId,
        character_id: characterId
      }, { headers: getAuthHeaders() });
      
      toast.success("Actor unassigned");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unassign actor");
    }
  };

  const handleResendInvite = async (actorId) => {
    try {
      await axios.post(`${API}/director/resend-invite`, {
        actor_id: actorId
      }, { headers: getAuthHeaders() });
      
      toast.success("Invite resent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invite");
    }
  };

  const getActorStatus = (actor) => {
    // has_setup_account = true means they NEED to set up (pending)
    // has_setup_account = false means they HAVE set up (active)
    if (actor.has_setup_account) {
      return { label: "Pending Setup", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" };
    }
    return { label: "Active", color: "bg-green-500/20 text-green-400 border-green-500/30" };
  };

  // Filter characters for this project
  const projectCharacters = characters.filter(c => c.project_id === projectId);

  // Get characters assigned to an actor in this project
  const getActorProjectCharacters = (actor) => {
    return (actor.characters || []).filter(c => c.project_id === projectId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] p-4 md:p-8 animate-fadeIn">
      {/* Header */}
      <header className="max-w-5xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(`/project/${projectId}`)}
              className="text-zinc-400 hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Users className="w-6 h-6 text-indigo-400" />
                Cast & Crew
              </h1>
              <p className="text-sm text-zinc-500">{project?.name}</p>
            </div>
          </div>
          
          <Button 
            onClick={() => setInviteOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="invite-actor-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Actor
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto space-y-6">
        {/* Actors List */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-4">Your Actors</h2>
          
          {actors.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 mb-2">No actors invited yet</p>
                <p className="text-sm text-zinc-500">
                  Invite voice actors to collaborate on your project
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {actors.map(actor => {
                const status = getActorStatus(actor);
                const actorChars = getActorProjectCharacters(actor);
                
                return (
                  <Card key={actor.id} className="bg-zinc-900/50 border-zinc-800" data-testid={`actor-card-${actor.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-zinc-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-medium text-white">
                                {actor.name || actor.email}
                              </h3>
                              <Badge variant="outline" className={status.color}>
                                {status.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-zinc-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {actor.email}
                            </p>
                            
                            {/* Character assignments */}
                            {actorChars.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {actorChars.map(char => (
                                  <Badge 
                                    key={char.id} 
                                    variant="outline" 
                                    className="bg-indigo-500/10 text-indigo-400 border-indigo-500/30"
                                  >
                                    <User className="w-3 h-3 mr-1" />
                                    {char.name}
                                    <button
                                      onClick={() => handleUnassign(actor.id, char.id)}
                                      className="ml-1 hover:text-red-400"
                                      title="Unassign"
                                    >
                                      ×
                                    </button>
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResendInvite(actor.id)}
                            className="text-zinc-400 hover:text-white"
                            title="Resend invite email"
                            data-testid={`resend-invite-${actor.id}`}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedActor(actor);
                              setAssignOpen(true);
                            }}
                            className="text-zinc-400 hover:text-white"
                            data-testid={`assign-btn-${actor.id}`}
                          >
                            <Link2 className="w-4 h-4 mr-1" />
                            Assign
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        {/* Characters in this project */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-4">Characters in This Project</h2>
          
          {projectCharacters.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="py-8 text-center">
                <p className="text-zinc-400">No characters created yet</p>
                <Button
                  variant="link"
                  onClick={() => navigate(`/project/${projectId}/characters`)}
                  className="text-indigo-400"
                >
                  Go to Characters page →
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {projectCharacters.map(char => {
                const assignedActors = actors.filter(a => 
                  a.assigned_characters?.includes(char.id)
                );
                
                return (
                  <Card key={char.id} className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0">
                          {char.reference_images?.[0]?.file_url ? (
                            <img 
                              src={getAssetUrl(char.reference_images[0].file_url)}
                              alt={char.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="w-5 h-5 text-zinc-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-white">{char.name}</h4>
                          {assignedActors.length > 0 ? (
                            <p className="text-sm text-green-400">
                              <Check className="w-3 h-3 inline mr-1" />
                              {assignedActors.map(a => a.name || a.email).join(", ")}
                            </p>
                          ) : (
                            <p className="text-sm text-zinc-500">No actor assigned</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Invite Actor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Email *</Label>
              <Input
                type="email"
                placeholder="actor@example.com"
                value={inviteData.email}
                onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Assign to Characters (optional)</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto bg-zinc-800/50 rounded-lg p-3">
                {projectCharacters.length === 0 ? (
                  <p className="text-sm text-zinc-500">No characters in this project yet</p>
                ) : (
                  projectCharacters.map(char => (
                    <div key={char.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`char-${char.id}`}
                        checked={inviteData.character_ids.includes(char.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setInviteData({ 
                              ...inviteData, 
                              character_ids: [...inviteData.character_ids, char.id] 
                            });
                          } else {
                            setInviteData({ 
                              ...inviteData, 
                              character_ids: inviteData.character_ids.filter(id => id !== char.id) 
                            });
                          }
                        }}
                        data-testid={`invite-char-${char.id}`}
                      />
                      <Label htmlFor={`char-${char.id}`} className="text-zinc-300 text-sm cursor-pointer flex items-center gap-2">
                        {char.reference_images?.[0]?.file_url && (
                          <img 
                            src={getAssetUrl(char.reference_images[0].file_url)}
                            alt={char.name}
                            className="w-6 h-6 rounded object-cover"
                          />
                        )}
                        {char.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
              {inviteData.character_ids.length > 0 && (
                <p className="text-xs text-indigo-400">
                  {inviteData.character_ids.length} character{inviteData.character_ids.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="fullScript"
                checked={inviteData.can_see_full_script}
                onCheckedChange={(checked) => setInviteData({ ...inviteData, can_see_full_script: !!checked })}
                data-testid="invite-full-script-checkbox"
              />
              <Label htmlFor="fullScript" className="text-zinc-300 text-sm cursor-pointer">
                Allow actor to see full script (not just their lines)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={handleInvite}
              disabled={inviting}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="invite-submit-btn"
            >
              {inviting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Mail className="w-4 h-4 mr-2" />
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignOpen} onOpenChange={(open) => {
        setAssignOpen(open);
        if (!open) {
          setSelectedCharacterIds([]);
        }
      }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Assign Actor to Characters</DialogTitle>
          </DialogHeader>
          {selectedActor && (
            <div className="space-y-4 py-4">
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <p className="text-sm text-zinc-400">Actor</p>
                <p className="font-medium text-white">{selectedActor.name || selectedActor.email}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Select Characters</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto bg-zinc-800/50 rounded-lg p-3">
                  {projectCharacters
                    .filter(char => !selectedActor.assigned_characters?.includes(char.id))
                    .length === 0 ? (
                    <p className="text-sm text-zinc-500 text-center py-2">
                      Already assigned to all characters
                    </p>
                  ) : (
                    projectCharacters
                      .filter(char => !selectedActor.assigned_characters?.includes(char.id))
                      .map(char => (
                        <div key={char.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`assign-char-${char.id}`}
                            checked={selectedCharacterIds.includes(char.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedCharacterIds([...selectedCharacterIds, char.id]);
                              } else {
                                setSelectedCharacterIds(selectedCharacterIds.filter(id => id !== char.id));
                              }
                            }}
                            data-testid={`assign-char-${char.id}`}
                          />
                          <Label 
                            htmlFor={`assign-char-${char.id}`} 
                            className="text-zinc-300 text-sm cursor-pointer flex items-center gap-2"
                          >
                            {char.reference_images?.[0]?.file_url && (
                              <img 
                                src={getAssetUrl(char.reference_images[0].file_url)}
                                alt={char.name}
                                className="w-6 h-6 rounded object-cover"
                              />
                            )}
                            {char.name}
                          </Label>
                        </div>
                      ))
                  )}
                </div>
                {selectedCharacterIds.length > 0 && (
                  <p className="text-xs text-indigo-400">
                    {selectedCharacterIds.length} character{selectedCharacterIds.length > 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={handleAssign}
              disabled={assigning || selectedCharacterIds.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="assign-submit-btn"
            >
              {assigning && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Assign to {selectedCharacterIds.length || ''} Character{selectedCharacterIds.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
